import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import { TOGGLE_RIGHT_DRAWER_EVENT } from '../ThemeBase';

/**
  * @typedef {Object} SearchGroup
  * @param {string} name
  * @param {any[]} results
  */

@Component({
  template: '#wetheme-right-drawer-template',
})
class RightDrawer extends Vue {
  /** @type {boolean} */
  isOpen = false;

  /** @type {'cart' | 'search' | 'shop-now' | 'page'} */
  type = 'cart';

  /** @type {import('theme-checker/dist/shopify').Cart | null} */
  cart = null;

  /** @type {number | null} */
  lineQuantityUpdating = null;

  /** @type {string | null} */
  quickShopProductUrl = null;

  /** @type {string} */
  quickShopContent = '';

  /** @type {boolean} */
  searching = false;

  /** @type {string} */
  searchQuery = ''

  /** @type {SearchGroup[]} */
  searchGroups = [];

  /** @type {number | null} */
  searchChangeTimer = null;

  /** @type {string} */
  searchSummary = '';

  /** @type {boolean} */
  searchHasResults = false

  /** @type {number} */
  searchResultsNum = 0;

  /** @type {string} */
  giftNote = '';

  /** @type {boolean} */
  updatingGiftNote = false;

  /** @type {string} */
  @Prop({required: true})
  searchUrl;

  /** @type {string} */
  @Prop({required: false})
  isPredictive;

  // /** @type {string} */
  @Prop({required: false})
  languageUrl;

  data() {
    return {
      isOpen: false,
      type: 'cart',
      cart: null,
      lineQuantityUpdating: null,
      quickShopProductUrl: null,
      quickShopContent: '',
      searchQuery: '',
      searchResults: [],
      giftNote: ''
    }
  }

  @Watch('giftNote')
  onGiftNoteChange() {
    if (this.giftNoteChangeTimer) {
      window.clearTimeout(this.giftNoteChangeTimer);
    }
    this.giftNoteChangeTimer = window.setTimeout(() => {
      this.updatingGiftNote = true;
      this.updateGiftNote();
      this.giftNoteChangeTimer = null;
    }, 1000);
  }

  @Watch('searchQuery')
  onSearchChange() {
    if(this.isSearchPredictive) {
      if(this.searchQuery > '') {
        if (this.searchChangeTimer) {
          window.clearTimeout(this.searchChangeTimer);
        }
        this.searchChangeTimer = window.setTimeout(() => {
          this.performSearch();
          this.searchChangeTimer = null;
        }, 250);
      }
      else {
        this.searchResultsNum = 0;
        this.searchHasResults = false;
        this.searchGroups = [];
        this.searching = false;
        this.searchSummary = '';
      }
    }
  }

  /** @returns {import('../ThemeBase').default} */
  get theme() {
    // @ts-ignore
    return this.$root.theme;
  }

  mounted() {
    this.cartAction = document.getElementById('PageContainer').dataset.cartAction;
    document.documentElement.addEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleToggle);
    // A custom event to toggle the right drawer from within web components
    window.eventBus.on('toggle:right:drawer', this.handleToggle);
  }

  beforeDestroy() {
    document.documentElement.removeEventListener(TOGGLE_RIGHT_DRAWER_EVENT, this.handleToggle);
    window.eventBus.off('toggle:right:drawer', this.handleToggle);
  }

  handleToggle(event) {
    const { type, forceOpen, params } = event.detail ? event.detail : event;
    if (params) {
      if (params.url) {
        const url = params.url;
        if(type === 'shop-now') {
          if (this.quickShopProductUrl !== url) {
            this.getProductPage(url);
            this.quickShopProductId = url;
          }
        } else if (type === 'page') {
          this.getGenericPage(url);
        }
      }
      if (params.cart) {
        this.cart = params.cart;
        // Populate gift note if it exists
        if(params.cart.attributes.gift_note) {
          this.giftNote = params.cart.attributes.gift_note;
        }
        this.theme.updateCartDrawer(params.cart);
      }
    }

    this.toggle(type, forceOpen);
  }

  close() {
    document.body.classList.remove('js-drawer-open');
    document.body.classList.remove('js-drawer-open-right');
    this.isOpen = false;
  }

  open() {
    this.setSubtotal();
    document.body.classList.add('js-drawer-open');
    document.body.classList.add('js-drawer-open-right');
    this.isOpen = true;
    if (this.type === 'cart' && (this.cart === null || this.cartAction === 'show_added_message')) {
      this.fetchCart();
      this.$nextTick(() => ((/** @type {HTMLElement} */ (this.$refs.drawerClose)).focus()));
    }
    else if (this.type === 'search') {
      this.$nextTick(() => ((/** @type {HTMLElement} */ (this.$refs.searchText)).focus()));
    }
  }

  toggle(type, forceOpen = undefined) {
    if (forceOpen === true) {
      this.type = type;
      this.open();
    } else if (forceOpen === false) {
      this.close();
    } else if (this.isOpen) {
      this.close();
    } else {
      this.type = type;
      this.open();
    }
  }

  async fetchCart() {
    try {
      const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
      const response = await window.fetch(`${languageParam}/cart?view=compare`);
      if (!response.ok) {
        // FIXME error handling
      }
      const cart = await response.json();
      this.cart = cart;
      // Populate gift note if it exists
      if(cart.attributes.gift_note) {
        this.giftNote = cart.attributes.gift_note;
      }
      this.theme.updateCartDrawer(cart);
    } catch (e) {
      console.error('Unable to fetch cart: ', e);
      // FIXME error handling
    }
  }

  setSubtotal() {
    setTimeout(function(){
      //Show the sum of all the line item compare at prices before the subtotal in the drawer
      let priceNoDiscount = document.querySelectorAll('.price-no--discount');
      let lineItemPrices = document.querySelectorAll('.has--discount');
      let lineItemComparePrices = document.querySelectorAll('.line-item__compare-at-price');
      let copmarePriceDiv = /** @type {HTMLElement} */ (document.querySelector('.line__compare_price'));
      let cartTotal = /** @type {HTMLElement} */ document.querySelector('.cart__total');

      let cartTotalAmt = 0;
      if(cartTotal) cartTotalAmt = (parseFloat(cartTotal.innerHTML));

      let totalWithDiscount;

      if (!lineItemComparePrices || !lineItemPrices) return;

      if (lineItemComparePrices.length > 0 && lineItemPrices.length > 0) {
        //Get the sum of all compare at prices
        let total = 0;
        let compareSubtotal;
        for (let num = 0; num < lineItemComparePrices.length; num++) {
          const currentPrice = /** @type {HTMLElement} */ (lineItemComparePrices[num]);
          total += (parseFloat(currentPrice.innerText));
          compareSubtotal = total;
        }

        //Get the sum of discount prices
        let count = 0;
        let finalPriceTotal;
        for (let amount = 0; amount < lineItemPrices.length; amount++) {
          const currentTotalPrice = /** @type {HTMLElement} */ (lineItemPrices[amount]);
          count += (parseFloat(currentTotalPrice.innerText));
          finalPriceTotal = count;
        }

        //Get the sum of all line items that don't have discounts
        let price = 0;
        let totalPrice;
        for (let amt = 0; amt < priceNoDiscount.length; amt++) {
          const currFinalPrice = /** @type {HTMLElement} */ (priceNoDiscount[amt]);
          price += (parseFloat(currFinalPrice.innerText));
          totalPrice = price;
        }

        if (totalPrice > 0) {
          totalWithDiscount = compareSubtotal + totalPrice;
        } else {
          totalWithDiscount = compareSubtotal;
        }

        if (copmarePriceDiv) {
          copmarePriceDiv.innerHTML = this.wetheme.formatMoney(totalWithDiscount);
        }
      }
    }, 1500);
  }

  /**
   * Fetch page
   *
   * @param {string} url
   */
  async fetchPage(url) {
    const response = await window.fetch(url);
    const parser = new DOMParser();
    const doc = parser.parseFromString(await response.text(), 'text/html');
    const pageContent = doc.querySelector('.page-main--wrapper');
    return pageContent;
  }

  /**
   * Product page url
   *
   * @param {string} url
   */
  async getProductPage(url) {
    try {
      const pageContent = await this.fetchPage(url);
      if (!pageContent) {
        console.error("Fetched page content is undefined or null.");
        return;
      }
  
      const template = pageContent.querySelector('template[data-quick-view-product]');
      if (!template || !template.content) {
        console.error("Template or template content not found.");
        return;
      }
  
      const productDiv = template.content;
  
      // Ensure the target element exists
      const target = this.$refs.shopNowContent;
      if (!(target instanceof HTMLElement)) {
        console.error("Target element is not an HTMLElement.");
        return;
      }
  
      // Append child only if productDiv is a valid node
      if (productDiv instanceof Node) {
        target.appendChild(productDiv);
      } else {
        console.error("productDiv is not a valid Node.");
        return;
      }
  
      // window.Shopify.PaymentButton does not exist until the fetched
      // product page is fully loaded
      setTimeout(() => {
        if (window.Shopify && window.Shopify.PaymentButton) {
          window.Shopify.PaymentButton.init();
        }
      }, 1250);
    } catch (error) {
      console.error("Error in getProductPage:", error);
    }
  }  

  /**
   * Page url
   *
   * @param {string} url
   */
  async getGenericPage(url) {
    const pageContent = await this.fetchPage(url);
    const target = /** @type {HTMLElement} */ (this.$refs.pageContent);
    target.appendChild(pageContent);
  }

  numDifference(a, b) {
    if (a > b) {
      return a - b;
    } else {
      return b - a;
    }
  }

  get isGiftwrapEnabled() {
    return (this.theme.giftwrapProduct && this.theme.giftwrapEnabled);
  }

  /**
   * Is a given Product object the selected giftwrap product?
   *
   * @param {object} item
   */
  isGiftwrapProduct(item) {
    if(!this.isGiftwrapEnabled) return false;
    return item.handle == this.theme.giftwrapProduct;
  }

  /**
   * Remove giftwrap product when another product is removed
   *
   * @param {number} line 1-based index of a line
   * @param {number} quantity new quantity
   */
  async updateGiftwrap(line, quantity) {
    const thisProduct = this.cart.items[line - 1];

    if(!this.isGiftwrapEnabled) return line;

    if(thisProduct.properties) {

      if(this.theme.translations.giftwrap_label in thisProduct.properties) {
        // Find position of gift wrap item in the cart. Note this will break
        // if you ever add additional properties to the gift wrap product
        // such that it splits that product into > 1 cart line items.
        const giftwrapIndex = this.cart.items.map(e => e.handle).indexOf(this.theme.giftwrapProduct);

        // Continue only if gift wrap product is in the cart
        if(giftwrapIndex > -1) {
          let difference = this.numDifference(thisProduct.quantity, quantity);

          // Quantity = quantity of current gift wrap product minus the quantity
          // of the product being removed. There could be gift wrap for other
          // items.
          let newGiftwrapQuantity = 0;
          if(quantity > thisProduct.quantity) {
            newGiftwrapQuantity = (this.cart.items[giftwrapIndex].quantity + difference);
          }
          else if (quantity == 0) {
            // If new quantity is zero, the associated product is being removed.
            newGiftwrapQuantity = (this.cart.items[giftwrapIndex].quantity - thisProduct.quantity);
          }
          else {
            newGiftwrapQuantity = (this.cart.items[giftwrapIndex].quantity - difference);
          }

          const response = await window.fetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              'line': giftwrapIndex + 1,
              'quantity': newGiftwrapQuantity,
            }),
          });

          // Line is always 1-based so do not go below 1.
          if(newGiftwrapQuantity == 0 && line > 1) return line - 1;
          return line;
        }
        return line;
      }
    }
    return line;
  }

  /**
   * Update quantity of an item in cart
   *
   * @param {number} line 1-based index of a line
   * @param {number} quantity new quantity
   */
  async setQuantity(line, quantity) {
    this.lineQuantityUpdating = line;
    try {
      // If item has gift wrap, change gift wrap quantity too.
      line = await this.updateGiftwrap(line, quantity);
      this.lineQuantityUpdating = line;

      const response = await window.fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line,
          quantity,
        }),
      });
      const cart = await response.json();
      this.cart = cart;
      this.lineQuantityUpdating = null;
    } catch (e) {
      console.error('Unable to fetch cart: ', e);
      // FIXME error handling
    }

    this.fetchCart();
  }

  onSearchSubmit() {
    window.location.href = this.searchPageUrlWithQuery;
    return;
  }

  clearSearchInput() {
    this.searchQuery = '';
    (/** @type {HTMLElement} */ (this.$refs.searchText)).focus();
  }

  get isSearchPredictive() {
    return (this.isPredictive == 'true');
  }

  get searchUrlWithQuery() {
    if(this.isSearchPredictive) {
      return`${this.searchUrl}?q=${encodeURIComponent(this.searchQuery)}&resources[limit]=3&resources[limit_scope]=each`;
    }
    else {
      return`/search?q=${encodeURIComponent(this.searchQuery)}`;
    }
  }

  get searchPageUrlWithQuery() {
    const languageParam = !this.languageUrl || this.languageUrl == '/' ? '' : this.languageUrl;
    return`${languageParam}/search?q=${encodeURIComponent(this.searchQuery)}*`;
  }

  async performSearch() {
    this.searching = true;
    this.searchHasResults = false;
    this.searchResultsNum = 0;
    this.searchSummary = '';
    let data;
    try {
      const response = await fetch(`${this.searchUrlWithQuery}`);

      if (!response.ok) {
        // FIXME handle error
      }
      data = await response.json();

    } catch (e) {
      console.error("Search failed", e);
      // We'll redirect the user to /search page when search fails (for whatever reason)
      window.location.href = this.searchPageUrlWithQuery;
    }

    const resultGroups = new Map();

    // Loop over groups e.g. Products / Pages / Articles / Collections
    Object.keys(data.resources.results).forEach((resultType) => {
      let group = resultGroups.get(resultType);
      let result = data.resources.results[resultType];

      if (!group) {
        group = {
          name: {
            'queries': this.theme.translations.suggestions,
            'articles': this.theme.translations.articles,
            'pages': this.theme.translations.pages,
            'products': this.theme.translations.products,
            'collections': this.theme.translations.collections,
          }[resultType],
          results: [],
        };
        resultGroups.set(resultType, group);
      }

      // Format price.
      let resultWithPrice = result.map(item => {
        var temp = Object.assign({}, item);
        if(temp.price) {
          temp.price = this.theme.formatMoney(temp.price);
        }
        return temp;
      });

      group.results = resultWithPrice;

      if(group.results && group.results.length > 0) {
        this.searchResultsNum += result.length;
        this.searchHasResults = true;
      }
    });

    if (!this.searchHasResults && this.searchQuery > '') {
      this.searchSummary = this.theme.translations.no_results_html.replace('{{ terms }}', this.searchQuery);
    }
    this.searching = false;
    this.searchGroups = Array.from(resultGroups.values());
  }

  async updateGiftNote() {
    const response = await window.fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ attributes: { 'gift_note': this.giftNote } })
    });

    if (!response.ok) {
      // FIXME error handling
      console.log('Could not update gift note in cart drawer', response);
    }

    this.updatingGiftNote = false;
  }
}

export default RightDrawer;
